import {
  Box,
  Heading,
  Text,
  HStack,
  VStack,
  Badge,
  Button,
  IconButton,
  useColorModeValue,
  Collapse,
  Divider,
  SimpleGrid,
  Flex,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Spinner,
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, CheckCircleIcon } from '@chakra-ui/icons';
import { FileText, Download, Trash2, Clock, TrendingUp, CheckCircle, Target, ListTodo, Users } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { UnifiedMeeting } from '../../types';
import { Timestamp } from 'firebase/firestore';
import { format } from 'date-fns';
import { DepartmentCommunications } from './DepartmentCommunications';
import { SectionNotesDisplay } from './SectionNotesDisplay';
import { useUsers } from '../../Users/useUsers';

interface MeetingCardProps {
  meeting: UnifiedMeeting;
  onGenerate?: () => void;
  onDelete?: () => void;
  onExport?: () => void;
  isSuperUser?: boolean;
}

/**
 * Meeting Card Component
 * Expandable card showing meeting information with summary details
 */
export function MeetingCard({
  meeting,
  onGenerate,
  onDelete,
  onExport,
  isSuperUser = false,
}: MeetingCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { users } = useUsers();
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const headingColor = useColorModeValue('gray.900', 'white');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const bgColor = useColorModeValue('white', 'gray.800');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const expandedBg = useColorModeValue('gray.50', 'gray.900');
  const deptCommBg = useColorModeValue('blue.50', 'blue.900');
  const deptCommBorder = useColorModeValue('blue.300', 'blue.600');
  const takeawaysBg = useColorModeValue('amber.50', 'amber.900');
  const takeawaysBorder = useColorModeValue('amber.300', 'amber.600');
  const summary = meeting.summary;
  
  // Get status from summary or default to undefined
  const summaryStatus = summary?.status;
  const isGeneratingStatus = summaryStatus === 'pending' || summaryStatus === 'generating';
  const isFailedStatus = summaryStatus === 'failed';
  const isCompletedStatus = summaryStatus === 'completed' || (!summaryStatus && meeting.hasSummary);

  // Helper function to get user name from ID
  const getUserName = (userId: string | undefined): string => {
    if (!userId) return 'Unknown';
    const user = users.find(u => u.id === userId || u.uid === userId);
    return user?.name || user?.Name || userId;
  };

  const formatDate = (date: Date | string | Timestamp | null | undefined) => {
    if (!date) return 'No date';
    
    try {
      let dateObj: Date;
      if (date instanceof Date) {
        dateObj = date;
      } else if (date instanceof Timestamp) {
        dateObj = date.toDate();
      } else if (typeof date === 'object' && date !== null) {
        // TypeScript now knows date is an object (not null/undefined/string)
        const dateValue = date as NonNullable<typeof date>; // Assert non-null
        if ('toDate' in dateValue) {
          // Handle Firestore Timestamp-like objects
          dateObj = (dateValue as Timestamp).toDate();
        } else {
          dateObj = new Date();
        }
      } else {
        // Must be a string at this point
        dateObj = new Date(date);
      }
      
      // Validate the date
      if (isNaN(dateObj.getTime())) {
        console.error('Invalid date:', date);
        return 'Invalid date';
      }
      
      return format(dateObj, 'EEE, MMM d, yyyy');
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return 'Invalid date';
    }
  };

  const formatTime = (timestamp: Timestamp | Date | string | null | undefined) => {
    if (!timestamp) return 'N/A';
    
    let date: Date;
    try {
      if (timestamp instanceof Date) {
        date = timestamp;
      } else if (timestamp instanceof Timestamp) {
        date = timestamp.toDate();
      } else if (typeof timestamp === 'object' && timestamp !== null) {
        // TypeScript now knows timestamp is an object (not null/undefined/string)
        const timestampValue = timestamp as NonNullable<typeof timestamp>; // Assert non-null
        if ('toDate' in timestampValue) {
          // Handle Firestore Timestamp-like objects
          date = (timestampValue as Timestamp).toDate();
        } else {
          date = new Date();
        }
      } else {
        // Must be a string at this point
        date = new Date(timestamp);
      }
      
      // Validate the date
      if (isNaN(date.getTime())) {
        console.error('Invalid date:', timestamp);
        return 'Invalid date';
      }
      
      return format(date, 'h:mm a');
    } catch (error) {
      console.error('Error formatting time:', error, timestamp);
      return 'Invalid date';
    }
  };

  const getRocksCount = () => {
    if (!summary) return 0;
    if (Array.isArray(summary.rocksAchieved)) {
      return summary.rocksAchieved.length;
    }
    return 0;
  };

  const getIssuesCount = () => {
    if (!summary) return 0;
    if (Array.isArray(summary.issuesResolved)) {
      return summary.issuesResolved.length;
    }
    if (summary.issues_resolved_count !== undefined) {
      return summary.issues_resolved_count;
    }
    return 0;
  };

  const getActionItemsCount = () => {
    if (!summary) return 0;
    if (Array.isArray(summary.actionItemsCreated)) {
      return summary.actionItemsCreated.length;
    }
    return 0;
  };

  const getTotalIssuesDiscussed = () => {
    if (!summary) return 0;
    if (summary.total_issues_discussed !== undefined) {
      return summary.total_issues_discussed;
    }
    if (Array.isArray(summary.issuesDiscussed)) {
      return summary.issuesDiscussed.length;
    }
    return 0;
  };

  return (
    <Box
      bg={bgColor}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="md"
      overflow="hidden"
      shadow="sm"
      transition="all 0.2s"
      _hover={{ shadow: 'md' }}
    >
      {/* Header - Clickable if has summary */}
      <Box
        p={4}
        cursor={meeting.hasSummary ? 'pointer' : 'default'}
        onClick={() => meeting.hasSummary && setIsExpanded(!isExpanded)}
        _hover={meeting.hasSummary ? { bg: hoverBg } : {}}
        transition="background 0.2s"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={4} flex={1}>
            <Box
              w={10}
              h={10}
              borderRadius="full"
              bg={meeting.hasSummary ? 'blue.100' : 'gray.100'}
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <Icon
                as={FileText}
                w={5}
                h={5}
                color={meeting.hasSummary ? 'blue.600' : 'gray.400'}
              />
            </Box>

            <VStack align="start" spacing={1} flex={1}>
              <Heading size="md" color={headingColor}>
                {formatDate(meeting.meetingDate)}
              </Heading>
              <HStack spacing={4} fontSize="sm" color={textColor}>
                <HStack spacing={1}>
                  <Icon as={Clock} w={4} h={4} />
                  <Text>{formatTime(meeting.meetingTimestamp)}</Text>
                </HStack>
                {summary && (
                  <>
                    <HStack spacing={1}>
                      <CheckCircleIcon w={4} h={4} />
                      <Text>{getIssuesCount()} issues resolved</Text>
                    </HStack>
                  </>
                )}
              </HStack>
            </VStack>

            {summary && (
              <HStack spacing={6} mr={4}>
                <VStack spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="green.600">
                    {getRocksCount()}
                  </Text>
                  <Text fontSize="xs" color={textColor}>
                    Rocks
                  </Text>
                </VStack>
                <VStack spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="orange.600">
                    {getTotalIssuesDiscussed()}
                  </Text>
                  <Text fontSize="xs" color={textColor}>
                    Issues
                  </Text>
                </VStack>
                <VStack spacing={0}>
                  <Text fontSize="2xl" fontWeight="bold" color="blue.600">
                    {getActionItemsCount()}
                  </Text>
                  <Text fontSize="xs" color={textColor}>
                    Action Items
                  </Text>
                </VStack>
              </HStack>
            )}
          </HStack>

          <HStack spacing={2}>
            {isCompletedStatus ? (
              <>
                <Badge colorScheme="green" fontSize="sm">
                  <CheckCircleIcon w={3} h={3} mr={1} />
                  Summary Generated
                </Badge>
                <IconButton
                  aria-label="Export PDF"
                  icon={<Icon as={Download} w={4} h={4} />}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport?.();
                  }}
                />
                {isSuperUser && (
                  <IconButton
                    aria-label="Delete Summary"
                    icon={<Icon as={Trash2} w={4} h={4} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                  />
                )}
                <IconButton
                  aria-label={isExpanded ? 'Collapse' : 'Expand'}
                  icon={isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsExpanded(!isExpanded);
                  }}
                />
              </>
            ) : isGeneratingStatus ? (
              <>
                <Badge colorScheme="blue" fontSize="sm">
                  <Spinner size="xs" mr={1} />
                  {summaryStatus === 'pending' ? 'Pending...' : 'Generating...'}
                </Badge>
                {isSuperUser && (
                  <IconButton
                    aria-label="Delete Meeting"
                    icon={<Icon as={Trash2} w={4} h={4} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                  />
                )}
              </>
            ) : isFailedStatus ? (
              <>
                <Badge colorScheme="red" fontSize="sm">
                  Generation Failed
                </Badge>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGenerate?.();
                  }}
                >
                  Retry
                </Button>
                {isSuperUser && (
                  <IconButton
                    aria-label="Delete Meeting"
                    icon={<Icon as={Trash2} w={4} h={4} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                  />
                )}
              </>
            ) : (
              <>
                <Button
                  size="sm"
                  colorScheme="blue"
                  onClick={(e) => {
                    e.stopPropagation();
                    onGenerate?.();
                  }}
                  isDisabled={isGeneratingStatus}
                >
                  Generate Summary
                </Button>
                {isSuperUser && (
                  <IconButton
                    aria-label="Delete Meeting"
                    icon={<Icon as={Trash2} w={4} h={4} />}
                    size="sm"
                    variant="ghost"
                    colorScheme="red"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                  />
                )}
              </>
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Expanded Content */}
      <Collapse in={isExpanded} animateOpacity>
        {summary && (
          <Box borderTopWidth="1px" borderColor={borderColor} bg={expandedBg} p={6}>
            <VStack align="stretch" spacing={6}>
              {/* Collect all department communications */}
              {(() => {
                const allDeptComms: Array<{ sectionName: string; notes: any[] }> = [];
                if (summary.segueNotes?.department_communications && summary.segueNotes.department_communications.length > 0) {
                  allDeptComms.push({ sectionName: 'Segue', notes: summary.segueNotes.department_communications });
                }
                if (summary.scorecardNotes?.department_communications && summary.scorecardNotes.department_communications.length > 0) {
                  allDeptComms.push({ sectionName: 'Scorecard', notes: summary.scorecardNotes.department_communications });
                }
                if (summary.rockReviewNotes?.department_communications && summary.rockReviewNotes.department_communications.length > 0) {
                  allDeptComms.push({ sectionName: 'Rock Review', notes: summary.rockReviewNotes.department_communications });
                }
                if (summary.customerEmployeeHeadlines?.department_communications && summary.customerEmployeeHeadlines.department_communications.length > 0) {
                  allDeptComms.push({ sectionName: 'Customer/Employee Headlines', notes: summary.customerEmployeeHeadlines.department_communications });
                }
                if (summary.idsNotes?.department_communications && summary.idsNotes.department_communications.length > 0) {
                  allDeptComms.push({ sectionName: 'IDS Discussion', notes: summary.idsNotes.department_communications });
                }
                if (summary.concludeNotes?.department_communications && summary.concludeNotes.department_communications.length > 0) {
                  allDeptComms.push({ sectionName: 'Conclusion', notes: summary.concludeNotes.department_communications });
                }

                if (allDeptComms.length === 0) return null;

                return (
                  <Box
                    bg={deptCommBg}
                    borderRadius="md"
                    p={6}
                    borderWidth="2px"
                    borderColor={deptCommBorder}
                  >
                    <HStack spacing={3} mb={4} pb={3} borderBottomWidth="2px" borderColor={deptCommBorder}>
                      <Icon as={Users} w={6} h={6} color="blue.700" />
                      <Heading size="lg" color="blue.900">
                        Department Communications
                      </Heading>
                    </HStack>
                    <VStack align="stretch" spacing={4}>
                      {allDeptComms.map(({ sectionName, notes }) => (
                        <DepartmentCommunications
                          key={sectionName}
                          sectionName={sectionName}
                          notes={notes}
                        />
                      ))}
                    </VStack>
                  </Box>
                );
              })()}

              {/* Key Takeaways & Decisions */}
              {summary.concludeNotes?.general && summary.concludeNotes.general.length > 0 && (
                <Box
                  bg={takeawaysBg}
                  borderRadius="md"
                  p={6}
                  borderWidth="2px"
                  borderColor={takeawaysBorder}
                >
                  <HStack spacing={3} mb={4} pb={3} borderBottomWidth="2px" borderColor={takeawaysBorder}>
                    <Icon as={TrendingUp} w={6} h={6} color="amber.700" />
                    <Heading size="lg" color="amber.900">
                      Key Takeaways & Decisions
                    </Heading>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {summary.concludeNotes.general.map((note, idx) => (
                      <Box
                        key={`conclude-general-${idx}`}
                        bg={bgColor}
                        borderRadius="md"
                        p={3}
                        borderWidth="1px"
                        borderColor={borderColor}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ node, ...props }) => <Text color={textColor} fontSize="sm" mb={2} {...props} />,
                            strong: ({ node, ...props }) => <Text as="strong" fontWeight="bold" color={textColor} {...props} />,
                            em: ({ node, ...props }) => <Text as="em" fontStyle="italic" color={textColor} {...props} />,
                            ul: ({ node, children, ...props }) => (
                              <Box as="ul" pl={4} mb={2} color={textColor} style={{ listStyleType: 'disc' }} {...(props as any)}>
                                {children}
                              </Box>
                            ),
                            ol: ({ node, children, ...props }) => (
                              <Box as="ol" pl={4} mb={2} color={textColor} style={{ listStyleType: 'decimal' }} {...(props as any)}>
                                {children}
                              </Box>
                            ),
                            li: ({ node, ...props }) => <Box as="li" mb={1} color={textColor} fontSize="sm" {...(props as any)} />,
                            h1: ({ node, ...props }) => <Heading size="md" color={textColor} mb={2} {...props} />,
                            h2: ({ node, ...props }) => <Heading size="sm" color={textColor} mb={2} {...props} />,
                            h3: ({ node, ...props }) => <Heading size="xs" color={textColor} mb={2} {...props} />,
                            code: ({ node, ...props }) => <Text as="code" bg="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="xs" fontFamily="mono" color={textColor} {...props} />,
                            pre: ({ node, ...props }) => <Box as="pre" bg="gray.100" p={2} borderRadius="sm" overflowX="auto" mb={2} {...(props as any)} />,
                          }}
                        >
                          {note.content}
                        </ReactMarkdown>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Meeting Activity Summary */}
              <Box>
                <HStack spacing={3} mb={4}>
                  <Icon as={TrendingUp} w={6} h={6} color="gray.700" />
                  <Heading size="lg" color={headingColor}>
                    Meeting Activity Summary
                  </Heading>
                </HStack>
                <SimpleGrid columns={3} spacing={4}>
                  <Box
                    bg={bgColor}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="green.200"
                    textAlign="center"
                  >
                    <Icon as={Target} w={6} h={6} color="green.600" mx="auto" mb={2} />
                    <Text fontSize="2xl" fontWeight="bold" color="green.600" mb={1}>
                      {getRocksCount()}
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      Rocks Achieved
                    </Text>
                  </Box>
                  <Box
                    bg={bgColor}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="blue.200"
                    textAlign="center"
                  >
                    <Icon as={CheckCircle} w={6} h={6} color="blue.600" mx="auto" mb={2} />
                    <Text fontSize="2xl" fontWeight="bold" color="blue.600" mb={1}>
                      {getIssuesCount()}
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      Issues Resolved
                    </Text>
                  </Box>
                  <Box
                    bg={bgColor}
                    p={4}
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="purple.200"
                    textAlign="center"
                  >
                    <Icon as={ListTodo} w={6} h={6} color="purple.600" mx="auto" mb={2} />
                    <Text fontSize="2xl" fontWeight="bold" color="purple.600" mb={1}>
                      {getActionItemsCount()}
                    </Text>
                    <Text fontSize="sm" color={textColor}>
                      Action Items Created
                    </Text>
                  </Box>
                </SimpleGrid>
              </Box>

              <Divider />

              {/* Section Notes (Reference) */}
              <Box>
                <Heading size="md" color={headingColor} mb={4}>
                  Section Notes (Reference)
                </Heading>
                <SectionNotesDisplay summary={summary} />
              </Box>

              {/* Issues Resolved */}
              {Array.isArray(summary.issuesResolved) && summary.issuesResolved.length > 0 && (
                <Box>
                  <HStack spacing={2} mb={3}>
                    <Icon as={CheckCircle} w={5} h={5} color="green.600" />
                    <Heading size="md" color={headingColor}>
                      Issues Resolved
                    </Heading>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {summary.issuesResolved.map((issue, index) => {
                      // Handle both string and object formats for backward compatibility
                      const issueName = typeof issue === 'string' ? issue : issue.name;
                      const issueId = typeof issue === 'string' ? `issue-${index}` : (issue.id || `issue-${index}`);
                      const resolutionNotes = typeof issue === 'string' ? undefined : issue.resolutionNotes;
                      
                      return (
                        <Box
                          key={issueId}
                          bg={bgColor}
                          borderRadius="md"
                          p={4}
                          borderWidth="1px"
                          borderColor="green.200"
                        >
                          <Text fontWeight="medium" color={headingColor} mb={1}>
                            {issueName}
                          </Text>
                          {resolutionNotes && (
                            <Text fontSize="sm" color={textColor}>
                              {resolutionNotes}
                            </Text>
                          )}
                        </Box>
                      );
                    })}
                  </VStack>
                </Box>
              )}

              {/* Rocks Achieved */}
              {Array.isArray(summary.rocksAchieved) && summary.rocksAchieved.length > 0 && (
                <Box>
                  <HStack spacing={2} mb={3}>
                    <Icon as={Target} w={5} h={5} color="green.600" />
                    <Heading size="md" color={headingColor}>
                      Rocks Achieved
                    </Heading>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {summary.rocksAchieved.map((rock) => (
                      <Box
                        key={rock.id || rock.name}
                        bg={bgColor}
                        borderRadius="md"
                        p={4}
                        borderWidth="1px"
                        borderColor="green.200"
                      >
                        <Text fontWeight="medium" color={headingColor}>
                          {rock.name}
                        </Text>
                        {rock.owner && (
                          <Text fontSize="sm" color={textColor} mt={1}>
                            Owner: {getUserName(rock.owner)}
                          </Text>
                        )}
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              {/* Action Items Created */}
              {Array.isArray(summary.actionItemsCreated) && summary.actionItemsCreated.length > 0 && (
                <Box>
                  <HStack spacing={2} mb={3}>
                    <Icon as={ListTodo} w={5} h={5} color="blue.600" />
                    <Heading size="md" color={headingColor}>
                      Action Items Created
                    </Heading>
                  </HStack>
                  <VStack align="stretch" spacing={2}>
                    {summary.actionItemsCreated.map((actionItem) => (
                      <Box
                        key={actionItem.id || actionItem.title}
                        bg={bgColor}
                        borderRadius="md"
                        p={3}
                        borderWidth="1px"
                        borderColor="blue.200"
                      >
                        <Text color={headingColor}>{actionItem.title || actionItem.name}</Text>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              )}

              <Divider />

              {/* Executive Summary (Collapsible - Reference Only) */}
              {summary.summary && summary.summary.trim() && (
                <Accordion allowToggle>
                  <AccordionItem>
                    <AccordionButton>
                      <Box flex="1" textAlign="left">
                        <Heading size="sm" color={headingColor}>
                          Executive Summary (Reference)
                        </Heading>
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4}>
                      <Box
                        bg={bgColor}
                        borderWidth={1}
                        borderColor={borderColor}
                        borderRadius="md"
                        p={4}
                      >
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          components={{
                            p: ({ node, ...props }) => <Text color={textColor} fontSize="sm" mb={2} {...props} />,
                            strong: ({ node, ...props }) => <Text as="strong" fontWeight="bold" color={textColor} {...props} />,
                            em: ({ node, ...props }) => <Text as="em" fontStyle="italic" color={textColor} {...props} />,
                            ul: ({ node, children, ...props }) => (
                              <Box as="ul" pl={4} mb={2} color={textColor} style={{ listStyleType: 'disc' }} {...(props as any)}>
                                {children}
                              </Box>
                            ),
                            ol: ({ node, children, ...props }) => (
                              <Box as="ol" pl={4} mb={2} color={textColor} style={{ listStyleType: 'decimal' }} {...(props as any)}>
                                {children}
                              </Box>
                            ),
                            li: ({ node, ...props }) => <Box as="li" mb={1} color={textColor} fontSize="sm" {...(props as any)} />,
                            h1: ({ node, ...props }) => <Heading size="md" color={textColor} mb={2} {...props} />,
                            h2: ({ node, ...props }) => <Heading size="sm" color={textColor} mb={2} {...props} />,
                            h3: ({ node, ...props }) => <Heading size="xs" color={textColor} mb={2} {...props} />,
                            code: ({ node, ...props }) => <Text as="code" bg="gray.100" px={1} py={0.5} borderRadius="sm" fontSize="xs" fontFamily="mono" color={textColor} {...props} />,
                            pre: ({ node, ...props }) => <Box as="pre" bg="gray.100" p={2} borderRadius="sm" overflowX="auto" mb={2} {...(props as any)} />,
                          }}
                        >
                          {summary.summary}
                        </ReactMarkdown>
                      </Box>
                    </AccordionPanel>
                  </AccordionItem>
                </Accordion>
              )}

              {/* Actions */}
              <HStack spacing={2} justify="flex-end">
                <Button size="sm" variant="outline" onClick={onExport}>
                  Export PDF
                </Button>
              </HStack>
            </VStack>
          </Box>
        )}
      </Collapse>
    </Box>
  );
}

